import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Winner {
  playerName: string;
  payout: number;
  entryId: number;
  position: number;
}

interface BracketPayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  winners: Winner[];
  sideEventId: number;
  leagueId: number;
  onPayoutComplete: (winnerId: number, amount: number) => void;
}

interface PayoutState {
  [entryId: number]: {
    isProcessing: boolean;
    isCompleted: boolean;
    showConfirmation: boolean;
  };
}

const BracketPayoutModal: React.FC<BracketPayoutModalProps> = ({
  isOpen,
  onClose,
  winners,
  sideEventId,
  leagueId,
  onPayoutComplete
}) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<{ [entryId: number]: string }>({});
  const [payoutStates, setPayoutStates] = useState<PayoutState>({});

  const handlePayoutRecord = async (winner: Winner) => {
    const entryId = winner.entryId;
    
    // Set processing state
    setPayoutStates(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        isProcessing: true
      }
    }));

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api'}/admin/payouts/bracket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sideEventId,
          leagueId,
          entryId: winner.entryId,
          amount: winner.payout,
          notes: comments[entryId] || ''
        })
      });

      if (response.ok) {
        // Show confirmation
        setPayoutStates(prev => ({
          ...prev,
          [entryId]: {
            isProcessing: false,
            isCompleted: false,
            showConfirmation: true
          }
        }));

        // Hide confirmation and mark completed after 3 seconds
        setTimeout(() => {
          setPayoutStates(prev => ({
            ...prev,
            [entryId]: {
              isProcessing: false,
              isCompleted: true,
              showConfirmation: false
            }
          }));
          
          onPayoutComplete(entryId, winner.payout);
        }, 3000);

      } else {
        const errorData = await response.json();
        console.error('Payout failed:', errorData);
        alert(`Payout failed: ${errorData.error || 'Unknown error'}`);
        
        setPayoutStates(prev => ({
          ...prev,
          [entryId]: {
            ...prev[entryId],
            isProcessing: false
          }
        }));
      }
    } catch (error) {
      console.error('Error recording payout:', error);
      alert('Error recording payout. Please try again.');
      
      setPayoutStates(prev => ({
        ...prev,
        [entryId]: {
          ...prev[entryId],
          isProcessing: false
        }
      }));
    }
  };

  const handleCommentsChange = (entryId: number, value: string) => {
    setComments(prev => ({
      ...prev,
      [entryId]: value
    }));
  };

  if (!isOpen) return null;

  // Filter out completed winners
  const activeWinners = winners.filter(winner => !payoutStates[winner.entryId]?.isCompleted);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              💰 {t('payoutModals.bracketPayouts')}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-green-100 mt-2">
            {t('payoutModals.recordPayoutsForBracketWinners')}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {activeWinners.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('payoutModals.allPayoutsComplete')}</h3>
              <p className="text-gray-600">{t('payoutModals.allBracketWinnersPaid')}</p>
              <button
                onClick={onClose}
                className="mt-4 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {t('manageSideEvent.close')}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {activeWinners.map((winner, index) => {
                const entryId = winner.entryId;
                const state = payoutStates[entryId];
                
                return (
                  <div key={entryId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    {/* Winner Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">🏆</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">{winner.playerName}</h3>
                          <p className="text-sm text-gray-600">{t('payoutModals.bracketWinner')}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-600">
                          ${winner.payout.toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-500">{t('payoutModals.payoutAmount')}</div>
                      </div>
                    </div>

                    {/* Comments Section */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('payoutModals.commentsOptional')}
                      </label>
                      <textarea
                        value={comments[entryId] || ''}
                        onChange={(e) => handleCommentsChange(entryId, e.target.value)}
                        placeholder={t('payoutModals.addNotesAboutPayout')}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                        rows={2}
                        disabled={state?.isProcessing}
                      />
                    </div>

                    {/* Confirmation Message */}
                    {state?.showConfirmation && (
                      <div className="mb-4 p-3 bg-green-100 border border-green-400 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="font-medium">
                            {t('payoutModals.payoutRecordedSuccessfully', { amount: `$${winner.payout.toFixed(2)}`, name: winner.playerName })}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    <button
                      onClick={() => handlePayoutRecord(winner)}
                      disabled={state?.isProcessing || state?.showConfirmation}
                      className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {state?.isProcessing ? (
                        <>
                          <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('manageSideEvent.processing')}
                        </>
                      ) : state?.showConfirmation ? (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {t('payoutModals.recordedSuccessfully')}
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          {t('payoutModals.recordPayout', { amount: `$${winner.payout.toFixed(2)}` })}
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BracketPayoutModal;